import { OfficeRecord } from "@types";

type OfficeLocationRef = {
  id: string;
  slug: string;
};

const locationSlugMatchesQuery = (slug: string, querySlug: string): boolean => {
  if (slug === querySlug) {
    return true;
  }

  if (slug.startsWith(querySlug)) {
    const suffix = slug.slice(querySlug.length);
    if (suffix === "" || /^\d+$/.test(suffix)) {
      return true;
    }
  }

  if (querySlug.startsWith(slug)) {
    const suffix = querySlug.slice(slug.length);
    return suffix === "" || /^\d+$/.test(suffix);
  }

  return false;
};

/**
 * Matches a location by slug, including DatoCMS dedup suffixes
 * (e.g. hash `fidiaz-mechelen` → block slug `fidiaz-mechelen1`).
 */
export const matchOfficeLocationBySlug = <T extends OfficeLocationRef>(
  locations: T[],
  slugOrHash: string,
): T | undefined =>
  locations.find((location) => locationSlugMatchesQuery(location.slug, slugOrHash));

/** True when `querySlug` matches `canonicalSlug` (exact or numeric DatoCMS suffix). */
export const officeLocationSlugMatches = (canonicalSlug: string, querySlug: string): boolean =>
  locationSlugMatchesQuery(canonicalSlug, querySlug);

export const getOfficeLocationOptionValue = (
  office: OfficeRecord,
  location?: OfficeLocationRef,
): string => {
  const locations = office.officeLocations ?? [];
  if (locations.length <= 1) {
    return office.slug;
  }
  return location?.id ?? locations[0]?.id ?? office.slug;
};

/**
 * Finds a separate root office record for a brand + location
 * (e.g. brand `accountingteam` + location `heist-op-den-berg` → `accountingteam-heist-op-den-berg`).
 */
export const findBrandLocationOfficeRecord = (
  offices: OfficeRecord[],
  brandSlug: string,
  locationSlug: string,
): OfficeRecord | undefined =>
  offices.find((office) => {
    if (office.slug === brandSlug || !office.slug.startsWith(`${brandSlug}-`)) {
      return false;
    }
    return Boolean(matchOfficeLocationBySlug(office.officeLocations ?? [], locationSlug));
  });
